import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AutomataService,
  Automation,
  Choices,
  ChoicesInterface,
  OutputParameter,
  WorkflowStep,
} from '@vendasta/automata';
import { Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { TRIGGER_STEP_ID } from '../common/constants';
import { CurrentAutomationService } from '@galaxy/automata/shared';

@Injectable({ providedIn: 'root' })
export class TriggerService {
  get automationId(): string {
    return this.currentAutomationService.automationId;
  }

  constructor(
    private readonly currentAutomationService: CurrentAutomationService,
    private readonly translateService: TranslateService,
    private readonly automataService: AutomataService,
  ) {}

  connectTriggerStepsAndFilters(
    triggerChoices: ChoicesInterface,
    filterSteps: WorkflowStep[],
    a: Automation,
  ): WorkflowStep[] {
    const nextStepFromTrigger = triggerChoices.rules[0].next;
    const cachedFilterStepsLength = filterSteps?.length || 0;
    const existingFilterStepsLength = a.triggerFilters?.length || 0;

    if (existingFilterStepsLength > 0) {
      if (cachedFilterStepsLength > 0) {
        triggerChoices.rules[0].next = filterSteps[0].id;
        filterSteps[cachedFilterStepsLength - 1].choices.rules[0].next =
          a.triggerFilters[existingFilterStepsLength - 1].choices.rules[0].next;
        return filterSteps;
      }
      triggerChoices.rules[0].next = a.triggerFilters[existingFilterStepsLength - 1].choices.rules[0].next;
      return filterSteps;
    } else {
      if (cachedFilterStepsLength > 0) {
        triggerChoices.rules[0].next = filterSteps[0].id;
        filterSteps[cachedFilterStepsLength - 1].choices.rules[0].next = nextStepFromTrigger;
        return filterSteps;
      }
    }
    return [];
  }

  /**
   * Returns the root element of the workflow.
   * @param automation
   * @private
   */
  private getWorkflowRoot(automation: Automation): string | undefined {
    if (!automation.workflow) {
      return;
    }

    // Remove all but the root element.
    const options: Set<string> = new Set(automation.workflow.map((ws) => ws.id));
    automation.workflow.forEach((ws) => {
      options.delete(ws.choices.defaultNextStep);
      ws.choices.rules?.forEach((rule) => options.delete(rule.next));
    });
    return [...options][0];
  }

  saveTriggerStep(
    choices: ChoicesInterface,
    filterSteps: WorkflowStep[],
    taskDefinitionId: string,
    data: string,
    customOutputParameters: OutputParameter[] = [],
    notes?: string,
  ): Observable<boolean> {
    return this.automataService.getAutomation(this.automationId).pipe(
      map((a) => {
        let triggerStepName = a.triggerStep?.name;
        if (!triggerStepName) {
          triggerStepName = this.translateService.instant('AUTOMATIONS.COMMON.TRIGGER');
        }
        a.triggerStep = new WorkflowStep({
          id: TRIGGER_STEP_ID,
          taskDefinitionId: taskDefinitionId,
          data: data || '{}',
          choices: new Choices(choices),
          name: triggerStepName,
          customOutputParameters: customOutputParameters,
          notes: notes,
        });
        a.triggerFilters = this.connectTriggerStepsAndFilters(choices, filterSteps, a);

        // If there's a workflow, make sure to connect it.
        // This occurs when we replace a trigger.
        if (a.workflow?.length > 0) {
          const triggerFiltersLength = a.triggerFilters?.length;
          if (triggerFiltersLength > 0) {
            a.triggerFilters[triggerFiltersLength - 1].choices.rules[0].next = this.getWorkflowRoot(a);
          } else {
            a.triggerStep.choices.rules[0].next = this.getWorkflowRoot(a);
          }
        }

        return a;
      }),
      switchMap((a) => {
        const fieldMask = {
          paths: ['TriggerStep', 'TriggerFilters'],
        };
        return this.automataService.updateAutomation(a, fieldMask);
      }),
      tap(() => this.currentAutomationService.refreshAutomation()),
      take(1),
    );
  }
}
